import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { Globe, PieChart as PieChartIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Organizer } from '../../types/organizer';
import { Flag } from '../common/Flag';

type CountryData = {
  name: string;
  value: number;
  code: string;
  color: string;
};

type Props = {
  organizers: Organizer[];
};

const COLORS = [
  '#00897B', '#26A69A', '#4DB6AC', '#80CBC4', // Primary greens
  '#EF6C00', '#F57C00', '#FB8C00', '#FF9800', // Oranges
  '#1976D2', '#2196F3', '#42A5F5', '#64B5F6', // Blues
  '#C5A572', '#D4B483', '#E3C394', '#F2D2A5', // Golds
];

export const OrganizersCountryChart = ({ organizers }: Props) => {
  const { translations } = useLanguage();

  // Process data for chart
  const data: CountryData[] = useMemo(() => {
    // Count organizers by country
    const countryCounts: Record<string, { count: number, code: string }> = {};
    
    organizers.forEach(organizer => {
      if (organizer.country) {
        if (!countryCounts[organizer.country]) {
          countryCounts[organizer.country] = { count: 0, code: organizer.country };
        }
        countryCounts[organizer.country].count++;
      }
    });
    
    // Convert to array and sort by count (descending)
    const countryData = Object.entries(countryCounts).map(([code, { count }], index) => ({
      name: getCountryNameFromCode(code),
      value: count,
      code,
      color: COLORS[index % COLORS.length]
    }));
    
    return countryData.sort((a, b) => b.value - a.value);
  }, [organizers]);
  
  // Only show chart if we have country data
  if (data.length === 0) return null;

  // Get country name from code
  function getCountryNameFromCode(code: string): string {
    const countries: Record<string, string> = {
  'CH': 'Switzerland',
  'FR': 'France',
  'IT': 'Italy',
  'CA': 'Canada',
  'ES': 'Spain',
  'CN': 'China',
  'GB': 'UK',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'US': 'USA',
  'DE': 'Germany',
  'SE': 'Sweden',
  'NZ': 'New Zealand'
};

    return countries[code] || code;
  }

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Flag countryCode={data.code} size={16} />
            <p className="font-medium text-gray-900">{data.name}</p>
          </div>
          <p className="text-primary-600 font-semibold">{`${data.value} organizer${data.value !== 1 ? 's' : ''}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white p-6 rounded-lg shadow-sm"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary-50 rounded-lg">
          <Globe className="text-primary-500" size={20} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {translations?.organizerChart?.title || 'Organizers by Country'}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Chart */}
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-col justify-center">
          <div className="space-y-3">
            {data.map((entry, index) => (
              <div key={entry.code} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <div className="flex items-center gap-1.5">
                    <Flag countryCode={entry.code} size={16} />
                    <span className="text-sm">{entry.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium">{entry.value}</span>
                  <span className="text-xs text-gray-500">
                    ({((entry.value / organizers.length) * 100).toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};